import { useTranslation } from 'react-i18next';
import { UI_COLORS } from '../../utils/colors';
import PortalModal, { InfoRow } from './PortalModal';
import { formatDate, formatMoney, isLoadingDone, actualMeasure } from '../portalUtils';
import { QuantityPair } from './PortalQuantityCells';
import { isPieceUnit } from '../../utils/units';

/**
 * Detail for a customer PURCHASE (what they buy from the company). Order-shaped:
 * item quantity unit is `units` (plural). Trimmed fields only.
 */
const PortalPurchaseViewModal = ({ isOpen, onClose, order, loading, statusLabels = {} }) => {
  const { t } = useTranslation();
  const s = t('portal.purchases', { returnObjects: true });
  const c = t('portal.common', { returnObjects: true });
  const items = Array.isArray(order?.items) ? order.items : [];
  const statusText = order?.status ? (statusLabels[order.status] || order.status) : '';
  // Actuals only exist once the truck is loaded — a customer purchase follows the sales
  // order lifecycle ('order').
  const loaded = isLoadingDone(order?.status, 'order');

  return (
    <PortalModal
      isOpen={isOpen}
      onClose={onClose}
      loading={loading}
      title={`${s?.detailTitle || 'Order'} ${order?.order_number || ''}`.trim()}
      maxWidth="max-w-4xl"
    >
      {order && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <InfoRow label={s?.columns?.date || 'Date'} value={formatDate(order.date)} />
            <InfoRow label={s?.columns?.status || 'Status'} value={statusText} />
            <InfoRow label={s?.columns?.deliveryLocation || 'Delivery Location'} value={order.delivery_location_name} />
            <InfoRow label={s?.columns?.expectedDelivery || 'Expected Delivery'} value={formatDate(order.expected_delivery_date)} />
            <InfoRow label={s?.columns?.total || 'Total'} value={formatMoney(order.total_amount)} />
          </div>

          <div>
            <h3 className="text-sm font-bold mb-2" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{c?.items || 'Items'}</h3>
            <div className="overflow-x-auto rounded-lg border" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ backgroundColor: UI_COLORS.BACKGROUNDS.SECTION }}>
                    <th className="text-left px-3 py-2 font-semibold">{c?.product || 'Product'}</th>
                    <th className="text-left px-3 py-2 font-semibold">{c?.size || 'Size'}</th>
                    <th className="text-right px-3 py-2 font-semibold">{c?.ordered || 'Ordered'}</th>
                    <th className="text-right px-3 py-2 font-semibold">{c?.actual || 'Actual'}</th>
                    <th className="text-right px-3 py-2 font-semibold">{c?.price || 'Price'}</th>
                    <th className="text-right px-3 py-2 font-semibold">{c?.amount || 'Amount'}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr><td colSpan={6} className="px-3 py-4 text-center" style={{ color: UI_COLORS.TEXT.MUTED }}>{c?.noItems || 'No items'}</td></tr>
                  ) : items.map((it, i) => {
                    const unit = it.units || it.unit || '';
                    // Order lines do carry a per-line actual weight; prefer it, and fall
                    // back only to the other ACTUAL field — never to the ordered qty.
                    // A pcs line is counted, not weighed, so its actual MEASURE is the
                    // received piece count (actual_quantity), not the kg actual_weight —
                    // otherwise a weight would render under a "pcs" unit.
                    const actualMeasureValue = isPieceUnit(unit)
                        ? actualMeasure(it.actual_quantity, it.actual_pieces)
                        : actualMeasure(it.actual_weight, it.actual_quantity);
                    return (
                    <tr key={i} className="border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                      <td className="px-3 py-2">{it.product_name || '—'}</td>
                      <td className="px-3 py-2">{it.size_name || '—'}</td>
                      <td className="px-3 py-2 text-right">
                        {/* For a pcs line `quantity` holds the optional kg weight, so passing
                            it as the measure printed the weight under a "pcs" unit — the very
                            thing the Actual column beside this was fixed to avoid. */}
                        <QuantityPair
                          measure={isPieceUnit(unit) ? (it.quantity_in_pieces ?? it.quantity) : it.quantity}
                          pieces={it.quantity_in_pieces}
                          unit={unit}
                          piecesLabel={c?.pcs || 'pcs'}
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <QuantityPair
                          measure={actualMeasureValue}
                          pieces={it.actual_pieces}
                          unit={unit}
                          piecesLabel={c?.pcs || 'pcs'}
                          emptyNote={loaded ? null : (c?.awaitingLoading || 'After loading')}
                        />
                      </td>
                      <td className="px-3 py-2 text-right">{formatMoney(it.price)}</td>
                      <td className="px-3 py-2 text-right">{formatMoney(it.amount)}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </PortalModal>
  );
};

export default PortalPurchaseViewModal;
